import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { 
  Box, 
  CircularProgress, 
  Alert
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import axios from 'axios';
import { ReportContext } from '../Context';
import { SocketContext } from '../Context';
import { ScrollContext } from '../Context';
import { ThemeContext } from '../Context';
import { DocumentToolsContext } from '../Context';
import { buildPvBody, buildPvHeader } from '../../utils/pvDocument';
import EmptyReportGuide from './EmptyReportGuide';
import PvPreview from './PvPreview';
import FindReplacePanel from './FindReplacePanel';

// Le panneau Rechercher se place sous la barre de zoom de l'aperçu
const FIND_PANEL_TOP = 52;

function Read() {
  const { report } = useContext(ReportContext);
  const { socket } = useContext(SocketContext);
  const { targetRubric, scrollToBottomTrigger } = useContext(ScrollContext);
  const { theme } = useContext(ThemeContext);
  const { findReplaceRequest, openFindReplace, closeFindReplace, findStateRef } = useContext(DocumentToolsContext);
  
  const isDark = theme === 'dark';
  
  const previewRef = useRef(null);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Paramètres de zone repris dans l'en-tête et l'introduction
  useEffect(() => {
    axios.get('/api/settings')
      .then((response) => setSettings(response.data || {}))
      .catch((err) => {
        console.error('Erreur lors de la récupération des paramètres :', err);
        setSettings({});
      });
  }, []);

  // Charger les données du rapport
  useEffect(() => {
    if (!report) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/report/search`, {
          params: { number: report.number, year: report.year },
          headers: { Authorization: `Bearer ${token}` },
        });
        
        setReportData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données du rapport:', error);
        setError('Impossible de charger les rubriques du rapport. Veuillez réessayer.');
        setLoading(false);
      }
    };

    fetchReportData();
  }, [report]);

  // Écouter les mises à jour du rapport via WebSocket
  useEffect(() => {
    if (!socket || !report) return;

    const handleReportUpdate = (updatedReport) => {
      if (updatedReport.number === report.number && updatedReport.year === report.year) {
        setReportData(prevData => {
          if (!prevData) return null;
          return {
            ...prevData,
            ...updatedReport
          };
        });
      }
    };
    
    // Événements émis directement par un client
    const handleDirectUpdate = (data) => {
      if (data.reportId === `${report.number}-${report.year}`) {
        setReportData(prevData => {
          if (!prevData) return null;
          const updates = {};
          const fieldsToUpdate = [
            'rubrics', 'StartDate', 'EndDate', 'intervenants', 
            'unit', 'reportType', 'indice', 'personnesPhysiques', 
            'objets', 'vehicules'
          ];
          
          fieldsToUpdate.forEach(field => {
            if (data[field] !== undefined) {
              updates[field] = data[field];
            }
          });
          
          return {
            ...prevData,
            ...updates
          };
        });
      }
    };

    // Les salles du PV (joinReport/leaveReport) sont gérées par Core.js
    socket.on('reportUpdated', handleReportUpdate);
    socket.on('updateReport', handleDirectUpdate);

    return () => {
      socket.off('reportUpdated', handleReportUpdate);
      socket.off('updateReport', handleDirectUpdate);
    };
  }, [socket, report]);

  // Même description du PV que l'export Word (Download.js)
  const header = useMemo(
    () => (reportData && settings ? buildPvHeader(reportData, settings) : null),
    [reportData, settings]
  );
  const body = useMemo(
    () => (reportData && settings ? buildPvBody(reportData, settings) : null),
    [reportData, settings]
  );

  if (!report) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="info"
          sx={{
            backgroundColor: isDark ? 'rgba(79, 195, 247, 0.15)' : undefined,
            color: isDark ? '#4fc3f7' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4fc3f7' : undefined
            }
          }}
        >
          Veuillez sélectionner un rapport pour afficher son contenu.
        </Alert>
      </Box>
    );
  }

  if (loading || !settings) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <CircularProgress sx={{
          color: isDark ? '#4fc3f7' : undefined
        }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        backgroundColor: isDark ? 'rgb(38, 53, 65)' : 'inherit'
      }}>
        <Alert 
          severity="error"
          sx={{
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ff6b6b' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#ff6b6b' : undefined
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!reportData || !reportData.rubrics || reportData.rubrics.length === 0) {
    return (
      <EmptyReportGuide
        title="Document vide"
        icon={<DescriptionOutlinedIcon sx={{ fontSize: 40, color: '#fff' }} />}
        step3Text="Visionnez-les ici"
      />
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      {/* Le papier de l'aperçu reste blanc en mode nuit : surlignage du mode jour */}
      <FindReplacePanel
        request={findReplaceRequest?.view === 'read' ? findReplaceRequest : null}
        onClose={closeFindReplace}
        onModeChange={openFindReplace}
        sharedStateRef={findStateRef}
        containerRef={previewRef}
        isDark={isDark}
        highlightDark={false}
        offsetTop={FIND_PANEL_TOP}
      />
      <PvPreview
        header={header}
        body={body}
        isDark={isDark}
        targetRubric={targetRubric}
        scrollToBottomTrigger={scrollToBottomTrigger}
        containerRef={previewRef}
      />
    </Box>
  );
}

export default Read;
